import React from 'react';
import { Calendar, User, MessageSquare, ChevronRight, Clock, Star } from 'lucide-react';

const STATUS_CONFIG = {
  otvoreno: { label: 'Otvoreno', bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-200', dot: 'bg-blue-500' },
  'u toku': { label: 'U toku', bg: 'bg-orange-100', text: 'text-orange-700', border: 'border-orange-200', dot: 'bg-orange-500' },
  zatvoreno: { label: 'Zatvoreno', bg: 'bg-green-100', text: 'text-green-700', border: 'border-green-200', dot: 'bg-green-500' },
  open: { label: 'Otvoreno', bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-200', dot: 'bg-blue-500' },
  in_progress: { label: 'U toku', bg: 'bg-orange-100', text: 'text-orange-700', border: 'border-orange-200', dot: 'bg-orange-500' },
  closed: { label: 'Zatvoreno', bg: 'bg-green-100', text: 'text-green-700', border: 'border-green-200', dot: 'bg-green-500' },
};

function formatDate(dateStr) {
  if (!dateStr) return '';
  return new Date(dateStr)?.toLocaleDateString('sr-RS', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function formatRelativeTime(dateStr) {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr)?.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `pre ${mins} min`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `pre ${hours}h`;
  const days = Math.floor(hours / 24);
  return `pre ${days} dana`;
}

export default function NegotiationCard({ negotiation, onClick, counterpartyRating }) {
  const statusKey = negotiation?.status?.toLowerCase()?.replace(' ', '_') || 'otvoreno';
  const statusConf = STATUS_CONFIG?.[negotiation?.status?.toLowerCase()] || STATUS_CONFIG?.[statusKey] || STATUS_CONFIG?.otvoreno;

  const counterparty = negotiation?.counterpartyName || negotiation?.counterparty || 'Nepoznat korisnik';
  const offerName = negotiation?.offerName || negotiation?.voucherName || 'Nepoznat vaučer';
  const lastMessage = negotiation?.lastMessage || negotiation?.initialMessage || '';
  const updatedAt = negotiation?.updatedAt || negotiation?.createdAt;

  return (
    <div
      onClick={onClick}
      className="bg-white rounded-xl border border-gray-200 p-5 hover:shadow-md transition-all cursor-pointer group"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          {/* Offer name + status */}
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <h3 className="font-semibold text-gray-900 group-hover:text-teal-700 transition-colors truncate">
              {offerName}
            </h3>
            <span className={`flex-shrink-0 inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium border ${statusConf?.bg} ${statusConf?.text} ${statusConf?.border}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${statusConf?.dot}`} />
              {statusConf?.label}
            </span>
          </div>

          {/* Counterparty */}
          <div className="flex items-center gap-1.5 text-sm text-gray-500 mb-2">
            <User size={13} />
            <span>{counterparty}</span>
            {counterpartyRating?.average && (
              <span className="flex items-center gap-0.5 ml-1">
                {[1, 2, 3, 4, 5]?.map(star => (
                  <Star
                    key={star}
                    size={11}
                    className={star <= Math.round(counterpartyRating?.average) ? 'fill-current' : 'text-gray-300'}
                    style={star <= Math.round(counterpartyRating?.average) ? {color:'#CFB53B'} : {}}
                  />
                ))}
                <span className="text-xs text-gray-400 ml-0.5">{Number(counterpartyRating?.average)?.toFixed(1)}</span>
              </span>
            )}
          </div>

          {/* Last message preview */}
          {lastMessage && (
            <div className="flex items-start gap-1.5 text-sm text-gray-500">
              <MessageSquare size={13} className="mt-0.5 flex-shrink-0" />
              <p className="line-clamp-2">{lastMessage}</p>
            </div>
          )}
        </div>

        <ChevronRight size={18} className="text-gray-400 group-hover:text-teal-600 transition-colors flex-shrink-0 mt-1" />
      </div>

      {/* Footer */}
      <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-between text-xs text-gray-400">
        <div className="flex items-center gap-1">
          <Calendar size={11} />
          <span>Pokrenuto: {formatDate(negotiation?.createdAt)}</span>
        </div>
        {updatedAt && (
          <div className="flex items-center gap-1">
            <Clock size={11} />
            <span>{formatRelativeTime(updatedAt)}</span>
          </div>
        )}
      </div>
    </div>
  );
}
